'use client'

import { useCallback, useEffect, useRef, useState } from 'react'

import { IconChevLeft, IconChevRight, IconGallery } from '@/app/components/atoms/Icons'
import { Poster } from '@/app/components/atoms/Poster'
import { PopUpSection } from '@/app/components/shared/PopUpSection'
import type { PopUpSectionItem } from '@/app/components/shared/PopUpSection'
import { YoutubeVideoThumb } from '@/app/components/shared/YoutubeVideoThumb'
import { cx } from '@/app/lib/utils'

import styles from './MediaGallery.module.scss'
import { MediaGalleryItem } from './types'

interface MediaGalleryProps {
  items: MediaGalleryItem[]
  /** Nombre accesible del pop-up de galería (FR-009, contracts/popup-section-contract.md). */
  ariaLabel: string
}

/** Clase de orientación de una celda — vídeo siempre "wide" (16:9, ratio real de YouTube). */
function slideOrientationClass(item: MediaGalleryItem): string {
  const isVertical = item.kind === 'image' && item.orientation === 'vertical'
  return cx(styles.slide, isVertical ? styles.slideVertical : styles.slideWide)
}

/**
 * `MediaGallery` [shared] — fila de medios con scroll nativo (sin JS de
 * carrusel propio más allá del cálculo de posición — research.md §1), fiel a
 * `PDSCarousel`/`ProductGallery` de `eci-firefly-uikit`.
 *
 * **Agnóstica del origen de los datos**: recibe un array ya normalizado
 * (`MediaGalleryItem[]`) y no sabe de dónde viene. Cada consumidor aporta su
 * propio normalizador — `normalizeMultimediaItems` en el módulo CMS
 * «Multimedia», `normalizePerformanceGalleryItems` en la ficha de evento
 * (specs/043-pagina-de-producto). Por eso vive en `shared/` y no dentro de
 * `modules/`, que es exclusivo del catálogo CMS (Constitución, Principio V).
 *
 * Comportamiento:
 *
 * - Misma altura en toda la fila; ancho por celda derivado de su
 *   orientación (16:9 vs 4:5).
 * - Flechas overlay prev/next (círculo semitransparente, se ocultan por
 *   completo en los extremos — no solo deshabilitadas).
 * - Barra de posición continua a todo el ancho (sin segmentos con gap ni
 *   forma de píldora — un único trazo dividido en partes iguales, el activo
 *   en verde de marca), navegable.
 * - Botón "Ver galería (N)" fijo en la esquina inferior derecha del área de
 *   medios — solo a partir de 2 elementos (con 1 solo ítem no aporta nada:
 *   no hay nada más que ver).
 *
 * Abre `PopUpSection` con todos los ítems, desde la posición actualmente
 * visible (FR-008/FR-009).
 */
export function MediaGallery({ items, ariaLabel }: MediaGalleryProps) {
  const trackRef = useRef<HTMLUListElement | null>(null)

  const [current, setCurrent] = useState(0)
  const [pages, setPages] = useState(1)
  const [canPrev, setCanPrev] = useState(false)
  const [canNext, setCanNext] = useState(items.length > 1)
  const [popUpOpen, setPopUpOpen] = useState(false)

  // Nº de celdas alcanzables sin overshoot (cuyo `offsetLeft` real no supera
  // el scroll máximo) — con más de una celda visible por vista (desktop), es
  // menor que `items.length`: las últimas celdas no pueden alinearse al
  // borde izquierdo sin desplazamiento de más. Sirve a la vez de nº de
  // segmentos de la barra de posición y de límite superior para `goTo`.
  const reachableCount = useCallback((track: HTMLUListElement) => {
    const children = Array.from(track.children) as HTMLElement[]
    const maxScrollLeft = Math.max(0, track.scrollWidth - track.clientWidth)
    let n = children.length
    for (let i = 0; i < children.length; i++) {
      const child = children[i]
      if (child && child.offsetLeft > maxScrollLeft + 1) {
        n = i
        break
      }
    }
    return Math.max(1, n)
  }, [])

  const currentFromScroll = useCallback((track: HTMLUListElement, upTo: number) => {
    const children = Array.from(track.children) as HTMLElement[]
    let idx = 0
    for (let i = 0; i < upTo && i < children.length; i++) {
      const child = children[i]
      if (child && child.offsetLeft <= track.scrollLeft + 1) idx = i
    }
    return idx
  }, [])

  const sync = useCallback(() => {
    const track = trackRef.current
    if (!track || track.children.length === 0) return
    const pageCount = reachableCount(track)
    const idx = currentFromScroll(track, pageCount)
    const maxScrollLeft = Math.max(0, track.scrollWidth - track.clientWidth)
    setPages(pageCount)
    setCurrent(idx)
    setCanPrev(track.scrollLeft > 1)
    setCanNext(track.scrollLeft < maxScrollLeft - 1)
  }, [reachableCount, currentFromScroll])

  useEffect(() => {
    sync()
    const track = trackRef.current
    if (!track) return
    // Recalcula si el track cambia de tamaño (breakpoint, contenedor) sin scroll.
    const resizeObserver = new ResizeObserver(() => sync())
    resizeObserver.observe(track)
    return () => resizeObserver.disconnect()
  }, [sync])

  const goTo = useCallback((index: number) => {
    const track = trackRef.current
    const target = track?.children[index] as HTMLElement | undefined
    if (!track || !target) return
    track.scrollTo({ left: target.offsetLeft, behavior: 'smooth' })
  }, [])

  const navigate = useCallback(
    (direction: 1 | -1) => {
      const track = trackRef.current
      if (!track || track.children.length === 0) return
      const idx = currentFromScroll(track, track.children.length)
      goTo(Math.max(0, Math.min(idx + direction, track.children.length - 1)))
    },
    [currentFromScroll, goTo],
  )

  const popUpItems: PopUpSectionItem[] = items.map((item) =>
    item.kind === 'video'
      ? { id: item.id, kind: 'video', youtubeId: item.youtubeId, title: item.title }
      : { id: item.id, kind: 'image', src: item.src, alt: item.alt },
  )

  return (
    <div className={styles.root}>
      <div className={styles.mediaArea}>
        <ul ref={trackRef} className={styles.track} onScroll={sync} aria-label={ariaLabel}>
          {items.map((item) => (
            <li key={item.id} className={slideOrientationClass(item)}>
              {item.kind === 'video' ? (
                <YoutubeVideoThumb videoId={item.youtubeId} title={item.title} />
              ) : (
                <Poster src={item.src} alt={item.alt} className={styles.media} />
              )}
            </li>
          ))}
        </ul>

        <button
          type="button"
          className={cx(styles.nav, styles.navPrev)}
          onClick={() => navigate(-1)}
          disabled={!canPrev}
          aria-label="Anterior"
        >
          <IconChevLeft size={20} />
        </button>
        <button
          type="button"
          className={cx(styles.nav, styles.navNext)}
          onClick={() => navigate(1)}
          disabled={!canNext}
          aria-label="Siguiente"
        >
          <IconChevRight size={20} />
        </button>

        {items.length > 1 && (
          <button type="button" className={styles.galleryBtn} onClick={() => setPopUpOpen(true)}>
            <IconGallery size={20} />
            Ver galería ({items.length})
          </button>
        )}
      </div>

      {pages > 1 && (
        <div role="group" aria-label={ariaLabel} className={styles.trackBar}>
          {Array.from({ length: pages }, (_, index) => (
            <button
              key={index}
              type="button"
              aria-label={`Ir a la posición ${index + 1} de ${pages}`}
              aria-current={index === current ? 'true' : undefined}
              className={cx(styles.trackSegment, index === current && styles.trackSegmentActive)}
              onClick={() => goTo(index)}
            />
          ))}
        </div>
      )}

      {popUpOpen && (
        <PopUpSection
          items={popUpItems}
          initialIndex={current}
          ariaLabel={ariaLabel}
          onClose={() => setPopUpOpen(false)}
        />
      )}
    </div>
  )
}
